import mixpanel from 'trackers/mixpanel'
import type { Surface } from 'types/project'

export const ChartEvent = {
  ChartViewed: 'Chart Viewed',
  ChartRendered: 'Chart Rendered',
  ChartTypeChanged: 'Chart Type Changed',
  ChartMetricChanged: 'Chart Metric Changed',
  ChartTimeBucketChanged: 'Chart Time Bucket Changed',
  ChartPanelAction: 'Chart Panel Action',
  ChartDisplayModeChanged: 'Chart Display Mode Changed',
  ChartAddedToDashboard: 'Chart Added to Dashboard',
  DashboardCreatedFromChart: 'Dashboard Created from Chart',
  ChartsHelpOpened: 'Charts Help Opened',
} as const

export type ChartRenderStatus =
  | 'success'
  | 'empty'
  | 'validation_error'
  | 'backend_error'

export type ChartDisplayMode = 'chart' | 'split' | 'table'

export type ChartPanelActionKind = 'added' | 'removed' | 'duplicated'

export type ChartDashboardLocation = 'toolbar' | 'context_menu'

export type ChartDisplayModeTrigger = 'button' | 'keyboard'

export type ChartViewedSource = 'ask_yogi'

export interface ChartViewedProps {
  surface?: Surface
  source?: ChartViewedSource
  conversation_uuid?: string
  message_uuid?: string
}

export interface ChartRenderedProps {
  surface?: Surface
  chart_type: string
  status: ChartRenderStatus
  /** Validation rule ID or HTTP status. Set when status != success. */
  error_code?: string
  /** Set on charts originated in Ask Yogi (native AY render or click-through). */
  source?: ChartViewedSource
  conversation_uuid?: string
  message_uuid?: string
  /** False on re-renders triggered by user-driven chart edits. */
  is_initial_render?: boolean
}

export interface ChartTypeChangedProps {
  from_type: string
  to_type: string
}

export interface ChartMetricChangedProps {
  metric: string
}

export interface ChartTimeBucketChangedProps {
  time_bucket: 'day' | 'week' | 'month' | 'quarter' | 'year'
}

export interface ChartPanelActionProps {
  action: ChartPanelActionKind
  /** Resulting total panels on the canvas. */
  panel_count: number
}

export interface ChartDisplayModeChangedProps {
  mode: ChartDisplayMode
  trigger: ChartDisplayModeTrigger
}

export interface ChartAddedToDashboardProps {
  chart_type: string
  dashboard_name: string
  location: ChartDashboardLocation
}

export interface DashboardCreatedFromChartProps {
  chart_type: string
  location: ChartDashboardLocation
}

export type ChartsHelpOpenedProps = Record<string, never>

const CHART_SURFACE_DEFAULT: Surface = 'chart'

export const trackChartViewed = (props: ChartViewedProps = {}): void => {
  mixpanel.track(ChartEvent.ChartViewed, {
    surface: CHART_SURFACE_DEFAULT,
    ...props,
  })
}

export const trackChartRendered = (props: ChartRenderedProps): void => {
  mixpanel.track(ChartEvent.ChartRendered, {
    surface: CHART_SURFACE_DEFAULT,
    ...props,
  })
}

export const trackChartTypeChanged = (props: ChartTypeChangedProps): void => {
  mixpanel.track(ChartEvent.ChartTypeChanged, props)
}

export const trackChartMetricChanged = (
  props: ChartMetricChangedProps,
): void => {
  mixpanel.track(ChartEvent.ChartMetricChanged, props)
}

export const trackChartTimeBucketChanged = (
  props: ChartTimeBucketChangedProps,
): void => {
  mixpanel.track(ChartEvent.ChartTimeBucketChanged, props)
}

export const trackChartPanelAction = (props: ChartPanelActionProps): void => {
  mixpanel.track(ChartEvent.ChartPanelAction, props)
}

export const trackChartDisplayModeChanged = (
  props: ChartDisplayModeChangedProps,
): void => {
  mixpanel.track(ChartEvent.ChartDisplayModeChanged, props)
}

export const trackChartAddedToDashboard = (
  props: ChartAddedToDashboardProps,
): void => {
  mixpanel.track(ChartEvent.ChartAddedToDashboard, props)
}

export const trackDashboardCreatedFromChart = (
  props: DashboardCreatedFromChartProps,
): void => {
  mixpanel.track(ChartEvent.DashboardCreatedFromChart, props)
}

export const trackChartsHelpOpened = (
  props: ChartsHelpOpenedProps = {},
): void => {
  mixpanel.track(ChartEvent.ChartsHelpOpened, props)
}
